import { useEffect, useState } from "react";
import { Info, Pencil } from "lucide-react";

import { api, errMsg } from "../../../../lib/api.js";
import { logError } from "../../../../lib/logger.js";
import { useToast } from "../../../../context/ToastContext.js";
import { AsyncButton } from "../../../../components/ui/async-button.js";
import { Button } from "../../../../components/ui/button.js";
import { DateInput } from "../../../../components/ui/date-input.js";
import { Textarea } from "../../../../components/ui/textarea.js";
import { DropdownSelect } from "../../../../components/DropdownButton.js";
import { Field, LockedNotice, OutcomeBadge, SubPanel, fmtDate } from "../../shared.js";
import {
  PROBATION_DECISIONS,
  PROBATION_OUTCOME_LABELS,
  type ProbationDecision,
  type ProbationInfo,
  type ReviewCapabilities,
} from "../../types.js";

type AnchorProbation = Extract<ProbationInfo, { isThisReview: true }>;

const DECISION_OPTIONS = PROBATION_DECISIONS.map((o) => ({ value: o, label: PROBATION_OUTCOME_LABELS[o] }));

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function dayAfter(iso: string | null): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

// HR's binding probation decision (HR / superuser only — ProbationSection does
// not render this for anyone else; they see the status). Before any decision
// the fields are shown. Once one is recorded it is shown as fixed, and the
// fields open only through "Edit decision" (after an EXTEND, "Record next
// decision") — HR can change any decision, except while a PIP opened by a
// PIP decision is still running (the API explains).
export default function ProbationDecisionBlock({
  reviewId,
  probation: p,
  caps,
  onChanged,
}: {
  reviewId: string;
  probation: AnchorProbation;
  caps: ReviewCapabilities;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [outcome, setOutcome] = useState<ProbationDecision | "">("");
  const [extendToDate, setExtendToDate] = useState("");
  const [pipStartDate, setPipStartDate] = useState(todayIso());
  const [pipEndDate, setPipEndDate] = useState("");
  const [notes, setNotes] = useState("");

  // Reset after a save (a new decision, or a new end date after an EXTEND).
  useEffect(() => {
    setEditing(false);
    setOutcome("");
    setExtendToDate("");
    setPipStartDate(todayIso());
    setPipEndDate("");
    setNotes("");
  }, [p.outcome, p.outcomeAt, p.periodEnd]);

  async function save() {
    try {
      await api.put(`/performance/reviews/${reviewId}/probation-outcome`, {
        outcome,
        extendToDate: outcome === "EXTEND" ? extendToDate : null,
        pipStartDate: outcome === "PIP" ? pipStartDate : null,
        pipEndDate: outcome === "PIP" ? pipEndDate : null,
        notes: notes || null,
      });
      toast.show(
        outcome === "EXTEND"
          ? "Probation extended — the employee stays on probation"
          : outcome === "PIP"
            ? "PIP opened for this employee"
            : "Probation confirmed",
        "success",
      );
      onChanged();
    } catch (e) {
      logError("ProbationDecisionBlock.outcome", e);
      toast.show(errMsg(e) ?? "Could not record the decision", "error");
      throw e;
    }
  }

  const ready =
    outcome !== "" &&
    (outcome !== "EXTEND" || !!extendToDate) &&
    (outcome !== "PIP" || (!!pipStartDate && !!pipEndDate && pipEndDate > pipStartDate));

  return (
    <SubPanel title="HR decision" actions={p.outcome && <OutcomeBadge outcome={p.outcome} />}>
      {p.outcome && (
        <div className="mb-3 flex flex-col gap-1 text-[13px]">
          <p className="text-surface-500">
            Last decision: {PROBATION_OUTCOME_LABELS[p.outcome]} · {fmtDate(p.outcomeAt)}
          </p>
          {p.outcome === "EXTEND" && (
            <p>
              Probation extended to <strong>{fmtDate(p.extendToDate)}</strong>
              {p.extensionCount > 1 ? ` (extension ${p.extensionCount})` : ""}. The employee stays on probation.
            </p>
          )}
          {p.outcome === "PIP" && p.pipRecordId && <p>A performance improvement plan was opened.</p>}
          {p.notes && <p className="whitespace-pre-wrap text-surface-600">{p.notes}</p>}
        </div>
      )}

      {caps.canDecideProbation && p.outcome && !editing ? (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            <Pencil size={14} /> {p.outcome === "EXTEND" ? "Record next decision" : "Edit decision"}
          </Button>
        </div>
      ) : caps.canDecideProbation ? (
        <div className="flex flex-col gap-3">
          <Field label={p.outcome ? "New decision" : "Decision"}>
            <DropdownSelect
              value={outcome}
              onChange={(v) => setOutcome(v as ProbationDecision)}
              options={DECISION_OPTIONS}
              placeholder="Select the decision"
              ariaLabel="Decision"
              clearable={false}
              className="w-full justify-between"
              open={open}
              onToggle={() => setOpen((o) => !o)}
              onClose={() => setOpen(false)}
            />
          </Field>
          {outcome === "EXTEND" && (
            <Field label="New probation end date" hint="Must be later than the current end date.">
              <DateInput isUS value={extendToDate} onChange={setExtendToDate} min={dayAfter(p.periodEnd)} />
            </Field>
          )}
          {outcome === "PIP" && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="PIP start date">
                <DateInput isUS value={pipStartDate} onChange={setPipStartDate} />
              </Field>
              <Field label="PIP end date" hint="Must be after the start date.">
                <DateInput isUS value={pipEndDate} onChange={setPipEndDate} min={dayAfter(pipStartDate || null)} />
              </Field>
            </div>
          )}
          <Field label="Notes">
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
          {outcome === "PIP" && (
            <p className="flex items-start gap-2 rounded-md bg-[var(--color-info-bg-subtle)] px-3 py-2 text-[12px] text-[var(--color-info-fg)]">
              <Info size={14} className="mt-0.5 shrink-0" />
              This closes the probation and opens a PIP for these dates, seeded with this review&apos;s goals. The
              employee and their manager are notified.
            </p>
          )}
          <div className="flex justify-end gap-2">
            {p.outcome && (
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            )}
            <AsyncButton
              size="sm"
              disabled={!ready}
              confirm={{
                title: outcome ? `${PROBATION_OUTCOME_LABELS[outcome]}?` : "Record the decision?",
                description:
                  (p.outcome && p.outcome !== "EXTEND"
                    ? `This replaces the recorded decision (${PROBATION_OUTCOME_LABELS[p.outcome]}, ${fmtDate(p.outcomeAt)}). `
                    : "") +
                  (outcome === "EXTEND"
                    ? `Probation moves to ${fmtDate(extendToDate)}; the employee stays on probation.`
                    : outcome === "PIP"
                      ? `A PIP runs from ${fmtDate(pipStartDate)} to ${fmtDate(pipEndDate)}.`
                      : "This is the binding probation decision."),
                actionText: "Record decision",
              }}
              onClick={() => save()}
            >
              Record decision
            </AsyncButton>
          </div>
        </div>
      ) : (
        !p.outcome && <LockedNotice>No decision has been recorded yet.</LockedNotice>
      )}
    </SubPanel>
  );
}
